import React from 'react';
import { motion } from 'framer-motion';
import { BackgroundGrids } from './BackgroundGrids';
import { useTranslation } from '../../hooks/useTranslation';
import { Navbar } from '../navigation/Navbar';
import { ChevronDown } from 'lucide-react';

// Add CSS keyframes for the chevron animation
const chevronAnimationStyle = `
  @keyframes floatDown {
    0% {
      transform: translateY(0px);
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    50% {
      transform: translateY(12px);
      opacity: 0.9;
    }
    100% {
      transform: translateY(30px);
      opacity: 0;
    }
  }
  
  .chevron-animation {
    animation: floatDown 2.1s ease-out infinite;
  }
  
  .chevron-animation-delayed {
    animation: floatDown 2.1s ease-out infinite;
    animation-delay: 1.05s;
    transform: translateY(-24px);
  }
`;

interface HeroContentProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  parentRef: React.RefObject<HTMLDivElement | null>;
  showHeroProps?: boolean;
}

export const HeroContent: React.FC<HeroContentProps> = ({ 
  containerRef, 
  showHeroProps = true
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Add the style tag for the animation */}
      <style>{chevronAnimationStyle}</style>
      
      {/* Navbar */}
      <Navbar containerRef={containerRef} />

      {/* Hero Section Background and Title */}
      {showHeroProps && (
        <>
          <BackgroundGrids />
        </>
      )}

      {showHeroProps && (
        <>
          <div className="text-balance relative z-20 mx-auto mb-4 mt-4 max-w-4xl text-center text-3xl font-semibold tracking-tight text-gray-700 dark:text-neutral-300 md:text-7xl">
            <motion.h2>
              {t('landing.title')
                .split(" ")
                .map((word, index) => (
                  <motion.span
                    initial={{
                      filter: "blur(10px)",
                      opacity: 0,
                      y: 10,
                    }}
                    animate={{
                      filter: "blur(0px)",
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.05,
                    }}
                    className="inline-block"
                    key={index}
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
            </motion.h2>
          </div>
          <div className="relative z-20 mx-auto mt-4 max-w-lg px-4 text-center flex flex-col items-center justify-center">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.5 }}
              className="text-base/6 text-gray-600 dark:text-gray-200"
            >
              {t('landing.subtitle')}
            </motion.p>
            
            {/* Animated Chevron using CSS animation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.5 }}
              className="mt-4 h-12 flex flex-col items-center justify-center overflow-hidden"
            >
              <div className="relative h-8 w-6">
                {/* First Chevron */}
                <div className="chevron-animation absolute top-0 left-0 right-0 flex justify-center">
                  <ChevronDown 
                    strokeWidth={1.5} 
                    className="h-5 w-5 text-gray-600 dark:text-gray-300" 
                  />
                </div>
                
                {/* Second Chevron */}
                <div className="chevron-animation-delayed absolute top-0 left-0 right-0 flex justify-center">
                  <ChevronDown 
                    strokeWidth={1.5} 
                    className="h-5 w-5 text-gray-600 dark:text-gray-300" 
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </>
  );
};
